import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart3, Calendar, Loader2, MapPin, PhoneCall, RefreshCw, Route, Users, ListOrdered } from 'lucide-react';
import { Card, CardHeader, Button } from '../components/ui';
import { reportsApi, type Report, type ReportType, type SharedSavedReportsSnapshot } from '../lib/api';

type ReportTab = ReportType;

function formatNumber(value: number | undefined): string {
  return Number(value || 0).toLocaleString('ru-RU');
}

function formatPeriodLabel(period: string): string {
  const rangeMatch = period.match(/^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/);
  if (rangeMatch) {
    const from = new Date(`${rangeMatch[1]}T00:00:00`);
    const to = new Date(`${rangeMatch[2]}T00:00:00`);
    return `${from.toLocaleDateString('ru-RU')} - ${to.toLocaleDateString('ru-RU')}`;
  }

  const [year, month] = period.split('-');
  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
}

type KeywordPositionRow = {
  keyword: string;
  currentPosition: number;
  previousPosition: number;
};

function parseKeywordPositions(value: unknown): KeywordPositionRow[] {
  if (Array.isArray(value)) {
    return value as KeywordPositionRow[];
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as KeywordPositionRow[]) : [];
    } catch {
      return [];
    }
  }

  return [];
}

function pickPreferredTab(snapshot: SharedSavedReportsSnapshot, currentTab: ReportTab): ReportTab {
  if (snapshot.reportsByType[currentTab]) {
    return currentTab;
  }

  const orderedTabs: ReportTab[] = ['MAPS', 'ADS', 'KEYWORDS'];
  const firstExisting = orderedTabs.find((tab) => snapshot.reportsByType[tab]);
  return firstExisting || 'MAPS';
}

export function SharedClientReport() {
  const { token } = useParams<{ token: string }>();
  const [snapshot, setSnapshot] = useState<SharedSavedReportsSnapshot | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [activeTab, setActiveTab] = useState<ReportTab>('MAPS');
  const initializedTabRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (showLoader: boolean = false) => {
    if (!token) {
      setError('Некорректная ссылка');
      return;
    }

    try {
      if (showLoader) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }

      setError(null);
      const response = await reportsApi.getSharedSavedReports(token, {
        period: selectedPeriod || undefined,
      });

      const nextSnapshot = response.data.data;
      const nextPeriod = nextSnapshot.selectedPeriod || '';

      setSnapshot(nextSnapshot);
      if (!initializedTabRef.current) {
        setActiveTab(pickPreferredTab(nextSnapshot, 'MAPS'));
        initializedTabRef.current = true;
      }

      if (nextPeriod && nextPeriod !== selectedPeriod) {
        setSelectedPeriod(nextPeriod);
      }
    } catch (loadError: any) {
      setError(loadError?.response?.data?.error || 'Не удалось загрузить отчёт');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [selectedPeriod, token]);

  useEffect(() => {
    void loadData(true);
  }, [loadData]);

  const activeReport = useMemo<Report | null>(() => {
    if (!snapshot) {
      return null;
    }

    return snapshot.reportsByType[activeTab] || null;
  }, [activeTab, snapshot]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader
              title="Отчёт клиента"
              subtitle="Не удалось загрузить данные по ссылке"
            />
            <div className="text-red-600 text-sm">{error}</div>
          </Card>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        <Card>
          <CardHeader
            title={`Отчёты: ${snapshot.business.name}`}
            subtitle={`${snapshot.business.category}, ${snapshot.business.city}`}
            action={(
              <Button
                size="sm"
                variant="outline"
                onClick={() => void loadData(false)}
                icon={isRefreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              >
                Обновить
              </Button>
            )}
          />

          {snapshot.periods.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Период отчёта</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                >
                  {snapshot.periods.map((period) => (
                    <option key={period} value={period}>
                      {formatPeriodLabel(period)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Показываются сохранённые в сервисе отчёты
                </span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              Для этого бизнеса пока нет сохранённых отчётов.
            </div>
          )}
        </Card>

        {snapshot.periods.length > 0 && (
          <Card>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('MAPS')}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  activeTab === 'MAPS'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Карты
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('ADS')}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  activeTab === 'ADS'
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Реклама
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('KEYWORDS')}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  activeTab === 'KEYWORDS'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Ключевые запросы
              </button>
            </div>
          </Card>
        )}

        {snapshot.periods.length > 0 && !activeReport && (
          <Card>
            <div className="text-sm text-gray-500">
              Отчёт во вкладке не заполнен для выбранного периода.
            </div>
          </Card>
        )}

        {activeReport && activeTab === 'MAPS' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Card>
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  Просмотры карточки
                </div>
                <div className="text-2xl font-semibold text-gray-900">{formatNumber(activeReport.views)}</div>
              </Card>
              <Card>
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                  <PhoneCall className="w-4 h-4 text-emerald-500" />
                  Клики по номеру
                </div>
                <div className="text-2xl font-semibold text-gray-900">{formatNumber(activeReport.phoneCalls)}</div>
              </Card>
              <Card>
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                  <Route className="w-4 h-4 text-violet-500" />
                  Построено маршрутов
                </div>
                <div className="text-2xl font-semibold text-gray-900">{formatNumber(activeReport.routes)}</div>
              </Card>
              <Card>
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-500" />
                  Переходы на сайт
                </div>
                <div className="text-2xl font-semibold text-gray-900">{formatNumber(activeReport.websiteClicks)}</div>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Card>
                <div className="text-xs text-gray-500 mb-2">Показы в поиске</div>
                <div className="text-xl font-semibold text-gray-900">{formatNumber(activeReport.searchImpressions)}</div>
              </Card>
              <Card>
                <div className="text-xs text-gray-500 mb-2">Показы в категории</div>
                <div className="text-xl font-semibold text-gray-900">{formatNumber(activeReport.categoryImpressions)}</div>
              </Card>
              <Card>
                <div className="text-xs text-gray-500 mb-2">Трафик из поиска, %</div>
                <div className="text-xl font-semibold text-gray-900">{formatNumber(activeReport.trafficSearch)}</div>
              </Card>
              <Card>
                <div className="text-xs text-gray-500 mb-2">Трафик из карт, %</div>
                <div className="text-xl font-semibold text-gray-900">{formatNumber(activeReport.trafficMaps)}</div>
              </Card>
            </div>
          </>
        )}

        {activeReport && activeTab === 'ADS' && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card>
              <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-purple-500" />
                Показы рекламы
              </div>
              <div className="text-2xl font-semibold text-gray-900">{formatNumber(activeReport.adImpressions)}</div>
            </Card>
            <Card>
              <div className="text-xs text-gray-500 mb-2">Клики по рекламе</div>
              <div className="text-2xl font-semibold text-gray-900">{formatNumber(activeReport.adClicks)}</div>
            </Card>
            <Card>
              <div className="text-xs text-gray-500 mb-2">CTR, %</div>
              <div className="text-2xl font-semibold text-gray-900">{formatNumber(activeReport.adCtr)}</div>
            </Card>
            <Card>
              <div className="text-xs text-gray-500 mb-2">Расход, ₽</div>
              <div className="text-2xl font-semibold text-gray-900">{formatNumber(activeReport.adSpend)}</div>
            </Card>
          </div>
        )}

        {activeReport && activeTab === 'KEYWORDS' && (
          <Card>
            <CardHeader
              title="Позиции по ключевым запросам"
              subtitle={`Всего ключей: ${parseKeywordPositions(activeReport.keywordPositions).length}`}
            />

            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Ключевой запрос</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Позиция в радиусе</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Позиция в регионе</th>
                  </tr>
                </thead>
                <tbody>
                  {parseKeywordPositions(activeReport.keywordPositions).map((item, index) => (
                    <tr key={`${item.keyword}-${index}`} className="border-b border-gray-50 last:border-0">
                      <td className="py-3 px-4 text-sm text-gray-900">{item.keyword}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatNumber(item.currentPosition)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatNumber(item.previousPosition)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {parseKeywordPositions(activeReport.keywordPositions).length === 0 && (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <ListOrdered className="w-4 h-4" />
                Для выбранного периода не заполнены ключевые запросы.
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
